import moment from "moment";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import {
  defaultStockOutward,
  OutwardSource,
  OutwardType,
  OutwardTypeIntransit,
  StockOutwardItemMovementModel,
  StockOutwardModal,
  StockTransferAttachmentModel,
} from "../models/stock_transfer_model";
import { fetchGet, fetchPost, fetchPut } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import WarehouseSearchField from "../warehouse/warehouse_search_field";
import { UTCToLocalDate } from "../../../utils/date_util";
import ItemRowOutward from "./ItemRowOutward";
import PartySearchField from "../../accounts/parties/party_search_field";
import EmployeeSearchWidget from "../../../ui/new_components/emp_search_widget";
import { WarehouseModel } from "../models/warehouse_model";
import ModalDialog from "../../../ui/new_components/common/modal";
import Papa from "papaparse";
import MultiAttachmentSection, {
  attachment_category,
} from "../customer/components/MultiAttachmentSection";
import { file_dir } from "../../../utils/enums";
import {
  getAttachmentSignedURLAPI,
  uploadFiletoS3API,
} from "../customer/components/customer_repo";
import {
  getFileType,
  PurchaseOrderModelV2,
} from "../../purchase_order/model/purchase_order_model";
import { generate_id } from "../journals/destination_stock_voucher/components/item_row_status";
import { currencies } from "../../accounts/models/party_model";
import { GetOrderApi } from "../../../service/repos/order_repo";
import SearchFieldWidget from "../../../ui/new_components/search_field";
import { getOrgNamespaceApi } from "../../../service/repos/cms/org_repo";

interface StockJournalProps {}

const StockOutwardCreate: FunctionComponent<StockJournalProps> = () => {
  const { id } = useParams<{ id: string }>();

  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/stock_outward`;
  const [, setLoading] = useState(false);
  const [editable] = useState(false);
  const [addNewId1, setAddNewId1] = useState(Math.random() * 1000);
  const { showToast } = useToast();

  const [data, setData] = useState<StockOutwardModal>({
    ...defaultStockOutward,
  });
  const existingItems = data?.items || [];
  const [availableItems, setAvailableItems] = useState([]);
  const [showNonExistItemsModal, setShowNonExistItemsModal] = useState(false);
  const [nonExistItems, setNonExistItems] = useState<string[]>([]);

  const [namespaces, setNameSpace] = useState<
    {
      name: string;
      namespace: string;
    }[]
  >([]);

  // const warehouseItems: any;
  const navigate = useHistory();


  useEffect(() => {
    if (id) getVoucher(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger file input
    }
  };

  const fetchPurchaseOrderByPoNo = async (
    poNo: string,
  ): Promise<PurchaseOrderModelV2 | null> => {
    try {
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/${poNo}`,
      );
      if (res.success) {
        if (!res.data.vendor_currency) {
          res.data.vendor_currency = currencies.rupee;
          res.data.vendor_currency_to_inr = 1;
        }
        if (!res.data.items) res.data.items = [];
        else
          setData((prev) => ({
            ...prev,
            items: res.data.items,
          }));
        return res.data as PurchaseOrderModelV2;
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: (error as Error).message });
      return null;
    }
  };

  const outwardType = data.outward_type;

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv"].includes(ext ?? "") || !file.type.includes("csv")) {
      showToast({ type: ToastType.error, text: "Only CSV files allowed." });
      event.target.value = "";
      return;
    }
    if (!data.warehouse?.name) {
      showToast({ type: ToastType.error, text: "Select a warehouse first." });
      event.target.value = "";
      return;
    }

    let rowIndex = 1;
    const invalidRows: number[] = [];

    setLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      worker: true,
      step: (result) => {
        rowIndex++;
        const row = result.data as Record<string, any>;
        const item = row["Item Code"]?.toString().trim();
        const device = row["Device ID"]?.toString().trim();

        if (!item || !device) {
          invalidRows.push(rowIndex);
        }
      },
      complete: async () => {
        if (invalidRows.length) {
          showToast({
            type: ToastType.error,
            text: `Mandatory fields missing at lines: ${invalidRows.join(
              ", ",
            )}`,
          });
          setLoading(false);
          event.target.value = "";
          return;
        }

        const formData = new FormData();
        formData.append("warehouse", data.warehouse.name);
        if (data.warehouse.id != null) {
          formData.append("from_warehouse_id", String(data.warehouse.id));
        }
        formData.append("file", file, file.name);

        try {
          const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/sender_transfer/import-items`;
          const resp = await fetch(url, { method: "POST", body: formData });

          const result = await resp.json();

          if (resp.ok && result.success) {
            setData((prev) => ({
              ...prev,
              items: [...prev.items, ...result.data],
            }));
            showToast({
              type: ToastType.success,
              text: result.message || "Upload successful!",
            });
          } else {
            showToast({
              type: ToastType.error,
              text: result.error || result.message || "Upload failed.",
            });
          }
        } catch (err: any) {
          showToast({
            type: ToastType.error,
            text: "Upload error: " + (err.message || err),
          });
        } finally {
          setLoading(false);
          event.target.value = "";
        }
      },
      error: (err) => {
        showToast({
          type: ToastType.error,
          text: "CSV parse error: " + err.message,
        });
        setLoading(false);
        event.target.value = "";
      },
    });
  };

  const handleDownloadTemplate = () => {
    const headers = ["Item Code", "Device ID"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "stock_outward_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (data.warehouse) {
      getItemsInSelectedWarehouse(data.warehouse);
    }
  }, [data.warehouse]);

  const getVoucher = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);
      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          ...res.data,
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to fetch voucher" });
    }
    setLoading(false);
  };

  const handleWarehouseSelect = async (selectedWarehouse: WarehouseModel) => {
    setData((prevData) => ({
      ...prevData,
      warehouse: selectedWarehouse,
    }));
    //call function to get list of items in warehouse.
    await getItemsInSelectedWarehouse(selectedWarehouse);
  };

  useEffect(() => {
    getOrgNamespaceApi()
      .then((val) => {
        if (val.success) {
          console.log(val.data);
          const allowedNamespaces = [
            "bigbasket_hulimavu",
            "no_pcb_devices_mahindra",
            "no_pcb_devices",
            "TVS_home_installation",
            "switch_mobility",
            "Matter_Energy",
            "Piaggio",
            "JBM",
            "transvolt",
            "synergyem",
            "greencell_mobility",
            "Montra_electric",
          ];

          const filteredNamespaces = val.data.filter(
            (org: { name: string; namespace: string }) =>
              allowedNamespaces.includes(org.namespace),
          );

          setNameSpace(filteredNamespaces);
        } else alert(val.error);
      })
      .catch((e) => alert(e.message));
  }, []);

  const getItemsInSelectedWarehouse = async (warehouse: WarehouseModel) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/warehouses?warehouse_id=${warehouse.id}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      if (res.success) {
        const { item_data } = res.data;
        console.log("item_data:", item_data, res);

        setAvailableItems(item_data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching warehouse stock value",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (
      data.source === OutwardSource["Stock inventory"] &&
      data.outward_type === OutwardType["Finished Good Sale"]
    ) {
      if (!data.org_namespace?.trim() || !data.org_name?.trim()) {
        showToast({
          type: ToastType.error,
          text: "Organisation cannot be empty.",
        });
        return;
      }
    }
    // Validate mandatory fields
    if (!data.items?.length || !data.outward_type || !data.outward_date) {
      return showToast({
        type: ToastType.error,
        text: "Please add items, outward type and date!!",
      });
    }

    // Conditional validation
    if (
      data.outward_type === OutwardType["Finished Good Sale"] &&
      !data.order_id
    ) {
      return showToast({
        type: ToastType.error,
        text: "Order ID is mandatory for Consumption type",
      });
    }

    // if (
    //   data.outward_type !== OutwardType["Finished Good Sale"] &&
    //   !data.po_no
    // ) {
    //   return showToast({
    //     type: ToastType.error,
    //     text: "PO No. is mandatory for non-Consumption type",
    //   });
    // }

    setLoading(true);

    const res = id
      ? await fetchPut(url + "/" + id, data)
      : await fetchPost(url, data);

    if (res.success) {
      showToast({ type: ToastType.success, text: res.message });
      navigate.push(urls.stock_outward);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }

    setLoading(false);
  };

  const uploadFile = async ({
    file,
    category,
  }: {
    file: File;
    category: string;
  }) => {
    try {
      if (!file || !category) {
        showToast({
          type: ToastType.warning,
          text: "Please select file and category",
        });
        return;
      }
      setLoading(true);
      const { name, type } = file;

      const query = {
        mimetype: type,
        extension: name.split(".").pop(),
        dir: file_dir.stock_outward_attachments,
      };
      const res = await getAttachmentSignedURLAPI(query);
      if (res.success) {
        const { signed_url, url } = res.data;

        await uploadFiletoS3API({
          signed_url,
          file,
          type: getFileType(type),
        });

        const attachment: StockTransferAttachmentModel = {
          id: generate_id(),
          category,
          type: getFileType(type),
          url,
          deleted: false,
        };
        showToast({
          type: ToastType.success,
          text: "Upload succesful",
        });

        setData((o: any) => {
          if (!o) return o; // Ensure o is not undefined
          return {
            ...o,
            attachments: [...(o.attachments || []), attachment],
          };
        });

        setLoading(false);
        return attachment;
      }
      setLoading(false);
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message,
      });
      setLoading(false);
    }
  };

  const onDeleteFile = (attachment: StockTransferAttachmentModel) => {
    setData((prev: any) => {
      if (!prev) return prev;

      const updatedAttachments = prev.attachments.map(
        (att: StockTransferAttachmentModel) =>
          att.id === attachment.id ? { ...att, deleted: true } : att,
      );

      return {
        ...prev,
        attachments: updatedAttachments,
      };
    });

    showToast({
      type: ToastType.success,
      text: "Attachment removed",
    });
  };

  return (
    <>
      {showNonExistItemsModal && (
        <ModalDialog
          show={showNonExistItemsModal}
          title="Items Not Exists"
          onClose={() => setShowNonExistItemsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Items</h2>
                <ul>
                  {nonExistItems.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}

      <div className="sticky top-0 mt-2 px-6 h-full">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Stock Outward Entry
          </div>
        </div>
        <div className="bg-white rounded pb-8 flex gap-1 h-auto overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 p-5 text-sm w-3/4 font-semibold rounded-t top-0">
            <div className="">
              <div className="mr-0">
                <div className="font-semibold">From Warehouse *</div>
                <WarehouseSearchField
                  type={""}
                  disabled={editable}
                  value={data.warehouse}
                  placeholder=""
                  onSelect={handleWarehouseSelect}
                  // onSelect={(d) => {
                  //   setData((old) => {
                  //     return {
                  //       ...old,
                  //       warehouse: d,
                  //     };
                  //   });
                  // }}
                />
              </div>
              <div className="flex flex-col my-2 gap-1">
                <label className="text-md font-semibold"> Source *</label>
                <select
                  value={data.source}
                  onChange={(e) => {
                    setData((old) => {
                      return {
                        ...old,
                        source: e.target.value,
                      };
                    });
                  }}
                  name=""
                  id="tax_type"
                  className="p-1 rounded  focus:outline-none w-full border"
                >
                  <option value="" hidden>
                    Select
                  </option>

                  {Object.values(OutwardSource).map((c, i) => (
                    <option value={c} key={i}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-md pt-1 font-semibold">
                  {" "}
                  Outward Type *
                </label>
                {data.source === OutwardSource["Stock inventory"] && (
                  <select
                    value={data.outward_type}
                    onChange={(e) => {
                      setData((old) => {
                        return {
                          ...old,
                          outward_type: e.target.value,
                        };
                      });
                    }}
                    name=""
                    id="tax_type"
                    className="p-1 rounded  focus:outline-none w-full border"
                  >
                    <option value="" hidden>
                      Select
                    </option>

                    {Object.values(OutwardType).map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
                {data.source === OutwardSource["In-transit"] && (
                  <select
                    value={data.outward_type}
                    onChange={(e) => {
                      setData((old) => {
                        return {
                          ...old,
                          outward_type: e.target.value,
                        };
                      });
                    }}
                    name=""
                    id="tax_type"
                    className="p-1 rounded  focus:outline-none w-full border"
                  >
                    <option value="" hidden>
                      Select
                    </option>

                    {Object.values(OutwardTypeIntransit).map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="my-2">
                <label htmlFor="" className="text-sm font-semibold">
                  Outward Date *
                </label>{" "}
                <input
                  type="date"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={
                    data?.outward_date
                      ? moment(data.outward_date).format("YYYY-MM-DD")
                      : "-"
                  }
                  //   min={moment().format("YYYY-MM-DD")}
                  onChange={(e) => {
                    const date = UTCToLocalDate(e.target.value)!;
                    setData((o) => ({
                      ...o,
                      outward_date: date,
                    }));
                  }}
                />
              </div>
              {/* {data.source === OutwardSource["In-transit"] && (
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="line1">
                    Stock Transfer ID *
                  </label>
                  <input
                    placeholder="stock_transfer_id"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="stock_transfer_id"
                    name="trstock_transfer_idack_id"
                    value={data.stock_transfer_id}
                    onChange={(e) =>
                      setData((prevData) => ({
                        ...prevData,
                        stock_transfer_id: e.target.value,
                      }))
                    }
                  />
                </div>
              )} */}
              <div className="my-2">
                <div className="font-semibold">Responsible Person</div>
                <EmployeeSearchWidget
                  withBtn={false}
                  onSelect={(em) => {
                    setData((c) => ({
                      ...c,
                      uid: em.uid!,
                      responsible_person: em.first_name + " " + em.last_name,
                    }));
                  }}
                  emp={
                    data.responsible_person
                      ? {
                          uid: data.uid,
                          name: data.responsible_person!,
                        }
                      : undefined
                  }
                />
                {/* <input
                  value={`${data.responsible_person?.first_name || ""} ${
                    data.responsible_person?.last_name || ""
                  }`}
                  placeholder="responsible_person"
                  type="text"
                  disabled={editable}
                  className="text-left p-1 border rounded focus:outline-none w-full"
                  onChange={(e) => {
                    setData((prevData) => ({
                      ...prevData,
                      responsible_person: {
                        ...prevData.responsible_person!,
                        first_name: e.target.value,
                        last_name: e.target.value,
                      },
                    }));
                  }}
                /> */}
              </div>
              <div className="my-2">
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="line1">
                    Responsible Person Address
                  </label>
                  <textarea
                    placeholder="address"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="address"
                    name="address"
                    rows={3}
                    value={data.responsible_person_address}
                    onChange={(e) =>
                      setData((prevData) => ({
                        ...prevData,
                        responsible_person_address: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div>
                <div className="font-semibold">Vendor</div>
                <PartySearchField
                  // show_add={true}
                  value={data.vendor_name}
                  onSelect={(d) => {
                    setData((o) => ({
                      ...o,
                      vendor_name: d,
                    }));
                  }}
                />
              </div>
              <div className="my-2">
                <label>Remark</label>
                <input
                  value={data?.remark}
                  name="remark"
                  id="remark"
                  placeholder="Remark"
                  type="text"
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  onChange={(e) =>
                    setData((d) => ({
                      ...d,
                      remark: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="">
              {/* {data.source === OutwardSource["In-transit"] && (
                <div className="">
                  <div className="font-semibold">In transit ID *</div>
                  <input
                    value={data.inTransit_id}
                    name="inTransit_id"
                    id="challinTransit_idan"
                    placeholder="inTransit_id"
                    type="text"
                    className="text-left p-1 focus:outline-none border rounded w-full"
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        inTransit_id: e.target.value,
                      }))
                    }
                  />
                </div>
              )} */}

              <MultiAttachmentSection
                attachments={data.attachments?.filter(
                  (attachment) =>
                    attachment.category === attachment_category.other &&
                    attachment.deleted === false,
                )}
                onSubmit={async function (data: {
                  file: File;
                  category: string;
                }) {
                  const attachment = await uploadFile({
                    file: data.file,
                    category: data.category,
                  });
                  if (!attachment) {
                    showToast({
                      type: ToastType.error,
                      text: "Upload failed",
                    });
                    throw new Error("File upload failed");
                  }
                  return attachment;
                }}
                category={attachment_category.other}
                onDelete={function (data: StockTransferAttachmentModel) {
                  onDeleteFile(data);
                }}
              />

              {outwardType !== OutwardType["Finished Good Sale"] && (
                <>
                  <div className="my-2">
                    <label className="font-semibold">PO No.</label>
                    <SearchFieldWidget
                      defaulValue={data.po_no}
                      placeholder="Enter PO Number"
                      onClear={() => {
                        setData((prev) => ({
                          ...prev,
                          po_no: "",
                          po_vendor_name: "",
                        }));
                      }}
                      onSubmit={async (val) => {
                        setData((prev) => ({ ...prev, po_no: val }));
                        if (val) {
                          const po = await fetchPurchaseOrderByPoNo(val);
                          if (po) {
                            setData((prev) => ({
                              ...prev,
                              po_vendor_name: po.party?.name || "",
                            }));
                          } else {
                            setData((prev) => ({
                              ...prev,
                              po_vendor_name: "",
                            }));
                          }
                        }
                      }}
                    />
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">PO Vendor</label>
                    <input
                      value={data.po_vendor_name || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>
                </>
              )}

              {outwardType === OutwardType["Finished Good Sale"] && (
                <>
                  <div className="my-2">
                    <label className="font-semibold">Order ID *</label>
                    <SearchFieldWidget
                      defaulValue={data.order_id}
                      placeholder="Enter Order ID"
                      onClear={() => {
                        setData((prev) => ({
                          ...prev,
                          order_id: "",
                          customer_name: "",
                          order_status: "",
                          sales_type: "",
                        }));
                      }}
                      onSubmit={async (val) => {
                        setData((prev) => ({ ...prev, order_id: val }));
                        if (val) {
                          const res = await GetOrderApi(val);
                          if (res.success && res.data) {
                            setData((prev) => ({
                              ...prev,
                              customer_name:
                                res.data.customer?.first_name +
                                  " " +
                                  res.data.customer?.last_name || "",
                              order_status: res.data.status || "",
                              sales_type: res.data.sale_type || "",
                            }));
                            setData((prev) => ({
                              ...prev,
                              items: res.data.products,
                            }));
                          } else {
                            setData((prev) => ({
                              ...prev,
                              customer_name: "",
                              order_status: "",
                              sales_type: "",
                            }));
                          }
                        }
                      }}
                    />
                  </div>

                  <div className="flex flex-col mt-3">
                    <label htmlFor="namespace">Organization*</label>
                    <select
                      required
                      name="namespace"
                      id="namespace"
                      className="w-full text-xs px-3 p-1 cursor-pointer bg-gray-100 rounded focus:outline-none"
                      value={JSON.stringify(
                        data.org_name
                          ? {
                              name: data.org_name,
                              namespace: data.org_namespace,
                            }
                          : {},
                      )}
                      onChange={(e) => {
                        const org = JSON.parse(e.target.value);
                        setData((o) => ({
                          ...o,
                          org_name: org.name,
                          org_namespace: org.namespace,
                        }));
                      }}
                    >
                      <option value="" hidden>
                        Select
                      </option>
                      {namespaces
                        .sort((a, b) =>
                          a.name
                            ? a.name.localeCompare(b.name)
                            : a.namespace.localeCompare(b.namespace),
                        )
                        .map((n, i) => (
                          <option key={i} value={JSON.stringify(n)}>
                            {n.name ?? n.namespace}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">Customer Name</label>
                    <input
                      value={data.customer_name || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">Order Status</label>
                    <input
                      value={data.order_status || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>

                  <div className="my-2">
                    <label className="font-semibold">Sales Type</label>
                    <input
                      value={data.sales_type || ""}
                      className="p-1 rounded border w-full"
                      disabled
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 mt-5 ">
          <div className="flex flex-col gap-5">
            <div className="">
              <div className="flex gap-2 items-center justify-between">
                <h3 className="text-sm font-bold">Items</h3>
                {data.source === OutwardSource["Stock inventory"] && (
                  <div className="">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                    />

                    <label
                      onClick={handleDownloadTemplate}
                      className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                    >
                      Template
                    </label>

                    {/* <label id="csvUpload"> */}
                    <button
                      onClick={handleButtonClick}
                      className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1"
                    >
                      Upload Product Items
                    </button>
                    {/* </label> */}
                  </div>
                )}
              </div>
              <div className="bg-white rounded flex flex-col gap-1 h-auto overflow-auto pb-20">
                {data.source === OutwardSource["In-transit"] && (
                  <div className="grid grid-cols-10 gap-1 p-1 bg-myPrimaryColor text-white text-sm font-semibold rounded-t sticky top-0">
                    <div className="text-left">InTransit ID</div>
                    <div className="text-left">Stock Transfer ID</div>
                    <div className="text-left">Item Code</div>
                    <div className="text-left col-span-2">Item Name</div>

                    <div className="text-left">Device IDs</div>
                    <div className="text-left">Qty</div>
                    <div className="text-left">Price</div>
                    <div className="text-left">Amount</div>
                    <div className="text-center">Actions</div>
                  </div>
                )}
                {data.source === OutwardSource["Stock inventory"] && (
                  <div className="grid grid-cols-9 gap-1 p-1 bg-myPrimaryColor text-white text-sm font-semibold rounded-t sticky top-0">
                    <div className="text-left">Item Code</div>
                    <div className="text-left col-span-2">Item Name</div>
                    <div className="text-left">Unit Measure</div>
                    <div className="text-left">Qty</div>
                    <div className="text-left">Device IDs</div>
                    <div className="text-left">Price</div>
                    <div className="text-left">Amount</div>
                    <div className="text-center">Actions</div>
                  </div>
                )}
                <div>
                  {data.items.map((comp, i) => {
                    return (
                      <ItemRowOutward
                        component={comp}
                        from_warehouse={data.warehouse}
                        // in_transit_id={data?.inTransit_id}
                        source={data.source}
                        edit={false}
                        disabled={false}
                        key={comp.id}
                        onDelete={(d: any) => {
                          setData((o) => {
                            const items = [
                              ...(o.items || []).filter((it) => it.id !== d.id),
                            ];

                            return {
                              ...o,
                              items,
                            };
                          });
                        }}
                        onSubmit={(d: any) => {
                          setData((o) => {
                            const items = [
                              ...(o.items || []).map((it) =>
                                it.id === d.id ? d : it,
                              ),
                            ];

                            return {
                              ...o,
                              items,
                            };
                          });
                        }}
                        existingItems={existingItems}
                        availableItems={availableItems}
                        outward_type={data.outward_type}
                      />
                    );
                  })}

                  {!data.id && (
                    <ItemRowOutward
                      key={addNewId1}
                      from_warehouse={data?.warehouse}
                      // in_transit_id={data?.inTransit_id}
                      source={data.source}
                      onSubmit={function (
                        data: StockOutwardItemMovementModel,
                      ): void {
                        data.id = (Math.random() * 1000).toString();
                        setData((o) => {
                          const items = [...(o.items || []), data];

                          return {
                            ...o,
                            items,
                          };
                        });
                        setAddNewId1(Math.random() * 1000);
                      }}
                      // index={i}
                      edit={true}
                      disabled={false}
                      existingItems={existingItems}
                      availableItems={availableItems}
                      outward_type={data.outward_type}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-52 right-8 bg-white z-10 flex justify-end gap-4 p-4">
        {!id && (
          <button
            type="button"
            className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
            onClick={submit}
            // disabled={isWithdrawn} // Disable submit if withdrawn or no items
          >
            Outward
          </button>
        )}
        {id && (
          <button
            type="button"
            className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
            onClick={submit}
            // disabled={isWithdrawn} // Disable submit if withdrawn or no items
          >
            Update
          </button>
        )}
        <button
          type="button"
          className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
          onClick={() => navigate.push(urls.stock_outward)}
        >
          Quit
        </button>
      </div>
      {/* )} */}
    </>
  );
};

export default StockOutwardCreate;
